import { CSSProperties, ReactNode } from "react";
import Card from "@rio-cloud/rio-uikit/Card";
import { Button } from "@rio-cloud/rio-uikit";
import { resolveAlertCriticality } from "@utils/alertCriticality";

export interface AlertsSummaryItem<TId extends string = string> {
  id: TId;
  label: string;
  count: number;
}

interface AlertsSummaryCardProps<TId extends string> {
  title: string;
  historyLabel: string;
  totalCount: number;
  items: AlertsSummaryItem<TId>[];
  onItemClick: (id: TId) => void;
  onHistoryClick: () => void;
  children?: ReactNode;
}

const RESET_LINHA: CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  textAlign: "inherit",
  width: "100%",
};

const DIVIDER_STYLE = { borderTop: "1px solid #E5EBF0" } as const;

const TitleCounter = ({ value }: { value: number }) => (
  <span className="bg-danger text-color-white text-medium text-size-20 display-flex align-items-center justify-content-center padding-x-10 rounded min-width-50 min-height-50">
    {value}
  </span>
);

const RowCounter = ({ value }: { value: number }) => (
  <span className="bg-lighter text-color-gray text-medium text-size-14 display-flex align-items-center justify-content-center padding-5 rounded width-30 height-30">
    {value}
  </span>
);

interface AlertRowProps {
  label: string;
  count: number;
  onClick: () => void;
}

const AlertRow = ({ label, count, onClick }: Readonly<AlertRowProps>) => (
  <button
    type="button"
    style={RESET_LINHA}
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick();
      }
    }}
    className="display-flex align-items-center gap-15 cursor-pointer"
  >
    <span
      className={`rioglyph rioglyph-warning-sign text-color-${resolveAlertCriticality(count)} text-size-14`}
      aria-hidden="true"
    />
    <span className="flex-1-1 min-width-0 text-size-14">{label}</span>
    <RowCounter value={count} />
  </button>
);

export default function AlertsSummaryCard<TId extends string>({
  title,
  historyLabel,
  totalCount,
  items,
  onItemClick,
  onHistoryClick,
  children,
}: Readonly<AlertsSummaryCardProps<TId>>) {
  const headerIconColor = totalCount === 0 ? "success" : "danger";

  return (
    <Card className="padding-10 padding-25-sm display-flex flex-column gap-10 gap-20-sm height-100pct border-style-solid border-color-danger border-width-3 border-left-only">
      <div className="display-flex align-items-center gap-15">
        <span
          className={`rioglyph rioglyph-warning-sign text-color-${headerIconColor} text-size-20`}
          aria-hidden="true"
        />
        <span className="flex-1-1 min-width-0 text-size-16 text-size-20-sm text-uppercase text-medium">{title}</span>
        <TitleCounter value={totalCount} />
      </div>

      <div className="display-flex flex-column gap-10 flex-1-1">
        {items.map((item) => (
          <div key={item.id} className="display-flex flex-column gap-10">
            <AlertRow label={item.label} count={item.count} onClick={() => onItemClick(item.id)} />
            <div style={DIVIDER_STYLE} />
          </div>
        ))}
      </div>

      <div className="display-flex justify-content-end">
        <Button bsStyle="primary" onClick={onHistoryClick}>
          <span>{historyLabel}</span>
          <span className="rioglyph rioglyph-arrow-right margin-left-10" aria-hidden="true" />
        </Button>
      </div>

      {children}
    </Card>
  );
}
